import type { Book } from '@/types/domain'
import { getSupabase } from './supabase'
import { useAuthStore } from './auth'
import { useSettingsStore } from '@/stores/settings'

/**
 * Índice de biblioteca en la nube.
 *
 * Cada libro subido tiene una fila ligera en `cloud_books` (metadatos +
 * progreso, sin el archivo) y el EPUB en Storage. Así un dispositivo
 * nuevo puede listar los libros disponibles y descargarlos bajo demanda.
 *
 * El progreso de lectura y las anotaciones se sincronizan aparte por
 * CRDT (doc por libro); aquí solo guardamos el índice para descubrir
 * qué libros tienes en la nube.
 */

const TABLE = 'cloud_books'

/** Fila del índice tal como vive en Supabase. */
export interface CloudBook {
  id: string
  user_id: string
  title: string
  author: string
  language: string | null
  isbn: string | null
  description: string | null
  tags: string[]
  collection: string | null
  status: string
  rating: number
  file_size: number
  progress_cfi: string | null
  progress_percent: number
  /**
   * Cuándo se leyó por última vez (`progress.lastReadAt` del `Book` local).
   * Antes de la migración 0020 esta columna no existía; los libros subidos
   * antes quedarán a `null` hasta que se reindexen (backfill).
   */
  last_read_at: string | null
  /** Fecha de finalización (`dateFinished` local). */
  date_finished: string | null
  /** Año de publicación extraído de `metadata.publishDate`. */
  publish_year: number | null
  /**
   * SHA-256 del EPUB (`Book.contentHash` local). Clave cross-device para
   * deduplicar la Biblioteca unificada cuando el mismo archivo se importó
   * en dos dispositivos (cada uno con su `id` distinto). `null` para los
   * libros subidos antes de la migración 0021.
   */
  content_hash: string | null
  updated_at: string
}

/** Lista todos los libros del usuario en la nube. */
export async function listCloudBooks(): Promise<CloudBook[]> {
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) return []
  const { data, error } = await client
    .from(TABLE)
    .select('*')
    .order('updated_at', { ascending: false })
  if (error) throw new Error(`Error al listar la nube: ${error.message}`)
  return (data ?? []) as CloudBook[]
}

/**
 * Lista los libros del usuario en la nube de forma paginada, con conteo
 * total y búsqueda opcional por título/autor.
 *
 * La paginación y la búsqueda se hacen en servidor (Supabase `.range` +
 * `.ilike` + `count: 'exact'`) para escalar a cientos/miles de títulos
 * sin traerlos todos al cliente. El filtro por usuario lo impone la RLS
 * de la tabla (`user_id`), así que el conteo es siempre del usuario.
 *
 * La búsqueda normaliza acentos (NFD + strip diacríticos) tanto en la
 * query como se aplica con `.or(title.ilike, author.ilike)` para no
 * depender del collation de la columna.
 */
export async function listCloudBooksPaged(opts: {
  /** Página base-1. */
  page: number
  /** Tamaño de página. */
  pageSize: number
  /** Texto de búsqueda opcional (title/author). */
  search?: string
}): Promise<{ items: CloudBook[]; total: number }> {
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) return { items: [], total: 0 }

  const { page, pageSize, search } = opts
  const from = (page - 1) * pageSize
  const to = from + pageSize - 1 // `.range` es inclusivo en ambos extremos.

  let query = client
    .from(TABLE)
    .select('*', { count: 'exact' })
    .order('updated_at', { ascending: false })
    .range(from, to)

  const q = normalizeSearch(search)
  if (q) {
    // Buscar en título o autor (insensible a mayúsculas/minúsculas).
    query = query.or(`title.ilike.%${q}%,author.ilike.%${q}%`)
  }

  const { data, count, error } = await query
  if (error) throw new Error(`Error al listar la nube: ${error.message}`)
  return { items: (data ?? []) as CloudBook[], total: count ?? 0 }
}

/**
 * Normaliza un texto para búsqueda: minúsculas, sin acentos ni diacríticos.
 * Para que "cafe" encuentre "Café" al usar ILIKE sobre columnas sin citext.
 */
function normalizeSearch(text: string | undefined): string {
  if (!text) return ''
  return text
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/**
 * Extrae el año (4 dígitos) de una fecha ISO `YYYY-MM-DD` o `YYYY-MM-DDTHH…`.
 * Devuelve `null` si no hay fecha o no se puede parsear. Es el mismo criterio
 * que usa el orden `year` del store (`library.ts`).
 */
export function parseYear(date: string | null | undefined): number | null {
  if (!date) return null
  const y = parseInt(date.slice(0, 4), 10)
  return Number.isNaN(y) ? null : y
}

/** Mapea un `Book` local a la fila que vive en `cloud_books`. */
function bookToCloudRow(book: Book, userId: string) {
  return {
    id: book.id,
    user_id: userId,
    title: book.title,
    author: book.author,
    language: book.metadata.language,
    isbn: book.metadata.isbn,
    description: book.metadata.description,
    tags: book.metadata.tags,
    collection: book.metadata.collection,
    status: book.status,
    rating: book.rating,
    file_size: book.fileSize ?? book.fileBlob?.size ?? 0,
    progress_cfi: book.progress.cfi,
    progress_percent: book.progress.percent,
    last_read_at: book.progress.lastReadAt ?? null,
    date_finished: book.dateFinished ?? null,
    publish_year: parseYear(book.metadata.publishDate),
    content_hash: book.contentHash ?? null,
    updated_at: new Date().toISOString(),
  }
}

/** Inserta o actualiza el índice de un libro al subirlo. */
export async function upsertCloudBook(book: Book): Promise<void> {
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) throw new Error('No hay sesión.')
  const { error } = await client
    .from(TABLE)
    .upsert(bookToCloudRow(book, auth.userId), { onConflict: 'id' })
  if (error) throw new Error(`Error al indexar el libro: ${error.message}`)
}

/**
 * Versión bulk de `upsertCloudBook`: inserta/actualiza el índice de varios
 * libros en una sola petición a `cloud_books`. Útil tras un upload masivo
 * de EPUBs para evitar N round-trips al índice.
 */
export async function upsertCloudBooks(books: Book[]): Promise<void> {
  if (books.length === 0) return
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) throw new Error('No hay sesión.')
  const rows = books.map((b) => bookToCloudRow(b, auth.userId!))
  const { error } = await client.from(TABLE).upsert(rows, { onConflict: 'id' })
  if (error) throw new Error(`Error al indexar los libros: ${error.message}`)
}

/** Elimina un libro del índice de la nube. */
export async function deleteCloudBook(bookId: string): Promise<void> {
  const client = requireClient()
  const { error } = await client.from(TABLE).delete().eq('id', bookId)
  if (error) throw new Error(`Error al quitar del índice: ${error.message}`)
}

/**
 * Elimina varios libros del índice `cloud_books` en una sola query con
 * `.in('id', ids)`. Más eficiente que N `deleteCloudBook` individuales.
 * Los objetos de Storage se borran aparte con `deleteEpubs`.
 */
export async function deleteCloudBooks(bookIds: string[]): Promise<void> {
  if (bookIds.length === 0) return
  const client = requireClient()
  const { error } = await client.from(TABLE).delete().in('id', bookIds)
  if (error) throw new Error(`Error al quitar del índice: ${error.message}`)
}

/**
 * Dado un conjunto de bookIds, devuelve cuáles de ellos están subidos a la
 * nube (existen en `cloud_books`). Una sola query con `.in('id', ids)`.
 *
 * Útil para el borrado en lote: saber de antemano cuántos de los
 * seleccionados tienen copia en la nube y ofrecer el checkbox "borrar
 * también de la nube". Devuelve un Set vacío si no hay sesión o sync.
 */
export async function getUploadedBookIds(bookIds: string[]): Promise<Set<string>> {
  if (bookIds.length === 0) return new Set()
  const client = requireClient()
  const auth = useAuthStore()
  if (!auth.userId) return new Set()
  const { data, error } = await client.from(TABLE).select('id').in('id', bookIds)
  if (error) {
    // Si falla la consulta, devolvemos vacío en vez de lanzar: el borrado
    // local no debería bloquearse por no poder consultar la nube.
    return new Set()
  }
  return new Set((data ?? []).map((r: { id: string }) => r.id))
}

/** Obtiene el cliente Supabase o lanza si no hay configuración. */
function requireClient() {
  const settings = useSettingsStore()
  const client = getSupabase(settings.settings.sync)
  if (!client) throw new Error('Supabase no configurado. Activa el sync en Ajustes.')
  return client
}
